import {AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {NgStyle} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ProductModel} from '../../../core/models/product.model';

export interface ToothSelectionEvent {
  tooth: number;
  count: number;
  shiftKey: boolean;
  ctrlKey: boolean;
  button: number;
}

@Component({
  selector: 'app-dental-arches',
  imports: [
    NgStyle,
    MatTooltipModule
  ],
  templateUrl: './dental-arches.component.html',
  styleUrl: './dental-arches.component.css'
})
export class DentalArchesComponent implements AfterViewInit, OnChanges {
  @ViewChild('chartRoot') chartRoot?: ElementRef<HTMLElement>;
  @Input() products: ProductModel[] = [];
  @Input() selectable = false;
  @Output() toothSelected = new EventEmitter<ToothSelectionEvent>();

  // Helper to get color for a tooth
  getToothColor(tooth: number): string {
    const product = this.products.find(p => p.tooth === tooth);
    return product ? product.color : '#fafafa'; // default fill
  }

  getToothTooltip(tooth: number): string {
    return this.products
      .filter(product => Number(product.tooth) === tooth)
      .map(product => product.name)
      .join(' + ');
  }

  ngAfterViewInit(): void {
    this.renderProductSegments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['products']) {
      queueMicrotask(() => this.renderProductSegments());
    }
  }

  @HostListener('mousedown', ['$event'])
  handleToothClick(event: MouseEvent): void {
    if (!this.selectable) return;

    const target = event.target as Element | null;
    const tooth = target?.closest('.tooth');
    const toothTitle = Number(tooth?.getAttribute('data-title'));
    const toothCount = Number(tooth?.getAttribute('data-count'));

    if (!tooth || Number.isNaN(toothTitle) || Number.isNaN(toothCount)) return;

    event.preventDefault();
    event.stopPropagation();
    this.toothSelected.emit({
      tooth: toothTitle,
      count: toothCount,
      shiftKey: event.shiftKey,
      ctrlKey: event.ctrlKey,
      button: event.button
    });
  }

  @HostListener('auxclick', ['$event'])
  preventAuxClick(event: MouseEvent): void {
    if (this.selectable) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private renderProductSegments(): void {
    const root = this.chartRoot?.nativeElement;
    if (!root) return;

    const svg = root.querySelector('svg');
    if (!svg) return;

    svg.querySelectorAll('.product-segment, .product-segment-definitions')
      .forEach(element => element.remove());

    root.querySelectorAll<SVGGElement>('.tooth').forEach(toothElement => {
      const tooth = Number(toothElement.dataset['title']);
      const fillPath = toothElement.querySelector<SVGPathElement>('.fil0');
      if (!fillPath || Number.isNaN(tooth)) return;

      const products = this.products.filter(product => Number(product.tooth) === tooth);
      fillPath.style.fill = products.length === 1 ? products[0].color : '#fafafa';
      if (products.length <= 1) return;

      const definitions = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      definitions.setAttribute('class', 'product-segment-definitions');
      svg.insertBefore(definitions, svg.firstChild);

      const bounds = fillPath.getBBox();
      const centerX = bounds.x + bounds.width / 2;
      const centerY = bounds.y + bounds.height / 2;
      const radius = Math.sqrt(bounds.width ** 2 + bounds.height ** 2) * .75;
      const anglePerProduct = Math.PI * 2 / products.length;

      products.forEach((product, index) => {
        const startAngle = -Math.PI / 2 + index * anglePerProduct;
        const endAngle = startAngle + anglePerProduct;
        const startX = centerX + radius * Math.cos(startAngle);
        const startY = centerY + radius * Math.sin(startAngle);
        const endX = centerX + radius * Math.cos(endAngle);
        const endY = centerY + radius * Math.sin(endAngle);
        const clipId = `angular-tooth-${tooth}-${products.length}-${index}`;
        const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
        const wedge = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        clipPath.id = clipId;
        wedge.setAttribute('d', `M ${centerX} ${centerY} L ${startX} ${startY} A ${radius} ${radius} 0 ${anglePerProduct > Math.PI ? 1 : 0} 1 ${endX} ${endY} Z`);
        clipPath.appendChild(wedge);
        definitions.appendChild(clipPath);

        const segment = fillPath.cloneNode(false) as SVGPathElement;
        segment.removeAttribute('id');
        segment.removeAttribute('style');
        segment.setAttribute('class', 'product-segment');
        segment.style.fill = product.color;
        segment.setAttribute('clip-path', `url(#${clipId})`);
        segment.style.pointerEvents = 'none';
        fillPath.parentNode?.insertBefore(segment, fillPath.nextSibling);
      });
    });
  }
}
